import { CircleCheck, LoaderCircle, TriangleAlert } from 'lucide-react';
import React from 'react';

import { Alert, AlertBody, AlertDescription, AlertIcon } from '@/ux-common/components/alert/alert';
import { Typography } from '@/ux-common/components/typography/typography';

import { getSaveProgressStats } from './save-progress.utils';
import type { PreviewSaveStatus } from './setting-preview.types';

interface SaveProgressSummaryProps {
  progressMap: Record<string, PreviewSaveStatus>;
}

/**
 * SaveProgressSummary
 *
 * Displays a single-line status banner summarizing the state of a concurrent
 * save operation. Renders one of three visual states:
 *
 * 1. **Active** (spinner) — save in progress, shows "Saving X/Y..." with
 *    optional breakdown of completed/failed so far.
 * 2. **All succeeded** (green) — every item completed without error.
 * 3. **Partial success** (caution) — save finished but some items failed or
 *    were cancelled. Shows "X/Y saved · Z failed · W cancelled".
 *
 * Returns `null` when no save has been initiated (empty progressMap).
 */
export const SaveProgressSummary = ({
  progressMap,
}: SaveProgressSummaryProps): React.ReactElement | null => {
  const { total, completed, failed, cancelled, settled, isActive } =
    getSaveProgressStats(progressMap);
  if (total === 0) return null;

  // ── State 1: All saved successfully (green banner) ──
  if (!isActive && failed === 0 && cancelled === 0 && completed === total) {
    return (
      <Alert className="alert-good" data-component="SaveProgressSummary">
        <AlertIcon>
          <CircleCheck />
        </AlertIcon>
        <AlertBody>
          <AlertDescription>All {total} changes saved successfully.</AlertDescription>
        </AlertBody>
      </Alert>
    );
  }

  // ── State 2: Complete with failures or cancellations (caution banner) ──
  if (!isActive && (failed > 0 || cancelled > 0)) {
    const lines: string[] = [];
    lines.push(`${completed}/${total} changes saved`);
    if (failed > 0) lines.push(`${failed} failed`);
    if (cancelled > 0) lines.push(`${cancelled} cancelled`);
    return (
      <Alert className="alert-caution" data-component="SaveProgressSummary">
        <AlertIcon>
          <TriangleAlert />
        </AlertIcon>
        <AlertBody>
          <AlertDescription>{lines.join(' · ')}</AlertDescription>
        </AlertBody>
      </Alert>
    );
  }

  // ── State 3: Active — save still in progress (spinner + counter) ──
  const parts: string[] = [];
  if (completed > 0) parts.push(`${completed} saved`);
  if (failed > 0) parts.push(`${failed} failed`);

  return (
    <Typography
      as="p"
      className="text-body-sm text-ckds-text-fg-tier2 flex items-center gap-2"
      data-component="SaveProgressSummary"
    >
      <LoaderCircle className="glyph glyph-sm animate-spin" aria-hidden="true" />
      Saving {settled}/{total}...
      {parts.length > 0 && ` (${parts.join(', ')})`}
    </Typography>
  );
};
